/**
 * WeatherOutlook — the demand forecast (Seth 2026-06-11, replacing the
 * "pointless" week strip). For an HVAC shop the forecast IS the workload
 * forecast: a 90°+ run means no-cool calls stack up; a hard freeze means
 * no-heat emergencies. Five days for the Twin Cities metro, with a plain
 * demand cue on extreme days. Data: Open-Meteo (free, no key) fetched from
 * the dispatcher's browser and cached for an hour.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Cloud,
  CloudDrizzle,
  CloudLightning,
  CloudRain,
  CloudSnow,
  Flame,
  Snowflake,
  Sun,
  ThermometerSun,
} from "lucide-react";

import { cn } from "@/lib/cn";

// Sedgwick's service area centroid (Twin Cities south metro).
const LAT = 44.86;
const LON = -93.3;

export interface DayForecast {
  date: string;
  high: number;
  low: number;
  precip: number; // %
  code: number; // WMO weather code
}

/** The metro forecast, `days` out (Open-Meteo goes to 16). Today's card reads
 *  five; the scheduling grid (SCHED-WEATHER-01, Seth 2026-09-22) reads
 *  sixteen so every day it can show has a high / low beside it. */
export function useForecast(days = 5) {
  return useQuery({
    queryKey: ["weather_outlook", days],
    staleTime: 60 * 60 * 1000,
    refetchInterval: 60 * 60 * 1000,
    queryFn: async (): Promise<DayForecast[]> => {
      const url =
        `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}` +
        `&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weather_code` +
        `&temperature_unit=fahrenheit&timezone=America%2FChicago&forecast_days=${Math.min(16, Math.max(1, days))}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error("weather unavailable");
      const j = (await r.json()) as {
        daily: {
          time: string[];
          temperature_2m_max: number[];
          temperature_2m_min: number[];
          precipitation_probability_max: number[];
          weather_code: number[];
        };
      };
      return j.daily.time.map((date, i) => ({
        date,
        high: Math.round(j.daily.temperature_2m_max[i]),
        low: Math.round(j.daily.temperature_2m_min[i]),
        precip: j.daily.precipitation_probability_max[i] ?? 0,
        code: j.daily.weather_code[i] ?? 0,
      }));
    },
  });
}

/** WMO weather code → icon. Coarse buckets are plenty for a glance. */
export function iconFor(code: number) {
  if (code >= 95) return CloudLightning;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 85) return CloudSnow;
  if (code >= 61 || (code >= 80 && code <= 82)) return CloudRain;
  if (code >= 51) return CloudDrizzle;
  if (code >= 1 && code <= 3) return Cloud;
  return Sun;
}

const DAY_FMT = new Intl.DateTimeFormat("en-US", { weekday: "short" });

/**
 * WeatherInline (TODAY-V5b, Seth 2026-09-30: "the weather demand placement
 * isn't great with the rest of the aesthetic") — the demand forecast left its
 * lonely right-rail card and became part of the day's context, sitting with
 * the date and the on-call pair. Five quiet day pills; a day that drives
 * demand (88°+ or a hard freeze) wears amber, and the surge note rides as
 * one chip instead of a paragraph. Same data, same hourly cache.
 */
export function WeatherInline({ className }: { className?: string }) {
  const { data, isError } = useForecast();

  // Quietly absent when the feed is down or still loading — never a hole.
  if (isError || !data) return null;

  const hot = data.filter((d) => d.high >= 88).length;
  const cold = data.filter((d) => d.low <= 5).length;

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <div
        className="flex items-center gap-1 rounded-full border border-hairline bg-white/70 px-2 py-1 shadow-sm"
        title="Five-day outlook for the metro — an 88°+ day means no-cool calls; a hard freeze means no-heat emergencies"
      >
        <ThermometerSun className="ml-0.5 h-3.5 w-3.5 shrink-0 text-sky-500" />
        {data.map((d, i) => {
          const Icon = iconFor(d.code);
          const extreme = d.high >= 88 || d.low <= 5;
          return (
            <span
              key={d.date}
              title={`${d.date} · high ${d.high}° low ${d.low}° · ${d.precip}% precip`}
              className={cn(
                "flex items-center gap-1 rounded-full px-1.5 py-0.5",
                extreme ? "bg-amber-50" : "",
              )}
            >
              <span className={cn("text-[10.5px] font-semibold", extreme ? "text-amber-700" : "text-slate-400")}>
                {i === 0 ? "Now" : DAY_FMT.format(new Date(`${d.date}T12:00:00`))}
              </span>
              <Icon className={cn("h-3.5 w-3.5", extreme ? "text-amber-500" : "text-slate-300")} />
              <span className={cn("text-[12px] font-bold tabular-nums", extreme ? "text-amber-800" : "text-slate-700")}>
                {d.high}°
              </span>
            </span>
          );
        })}
      </div>
      {(hot > 0 || cold > 0) && (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11.5px] font-semibold text-amber-800">
          {hot > 0 ? (
            <>
              <Flame className="h-3.5 w-3.5 shrink-0 text-amber-600" />
              {hot === 1 ? "One 88°+ day" : `${hot} days 88°+`} — keep same-day room
            </>
          ) : (
            <>
              <Snowflake className="h-3.5 w-3.5 shrink-0 text-sky-600" />
              Hard freeze ahead — keep same-day room
            </>
          )}
        </span>
      )}
    </div>
  );
}
